// Campaign automation journey — the contract shared by the API (save, enrollment, dispatcher) and the
// admin editor. A journey is a fixed set of steps (Welcome · Reminder · Follow-up · Last chance · On the day), each
// with an SMS and an Email message stored as independent `(step, channel)` rows with an `enabled` flag.
// The step fixes timing type, anchor and stop-if-booked, so no caller can make a step mean something else.
import { journeyReplyToBookSms, journeyStepDefaultCopy } from './automation-journey-default-copy-without-calendar';
import { shapeJourneyMessageDefaults, VOUCHER_MESSAGE_VARIABLES } from './automation-journey-shape-defaults';
import { JOURNEY_STEPS } from './automation-journey-steps';
import type { BookingLinkAction } from './booking-link-action';
import { GIVEAWAY_MESSAGE_VARIABLES } from './giveaways/giveaway-message-variables';
import type { CampaignShape } from './vouchers/voucher-enums';

// ---------------------------------------------------------------------------
// Enums (mirror the Prisma enums 1:1 — see schema.prisma).
// ---------------------------------------------------------------------------
// EVENT_DAY is appended last so every older step keeps its display order (and its `order` value).
export const MARKETING_AUTOMATION_STEPS = ['CONFIRMATION', 'REMINDER', 'WINBACK', 'LAST_CHANCE', 'EVENT_DAY'] as const;
export type MarketingAutomationStep = (typeof MARKETING_AUTOMATION_STEPS)[number];

export const MARKETING_SEND_CHANNELS = ['SMS', 'EMAIL'] as const;
export type MarketingSendChannel = (typeof MARKETING_SEND_CHANNELS)[number];

// IMMEDIATE = at form submit; DAYS_AFTER_SUBMIT = N days after submit; DAYS_BEFORE_EVENT = N days before
// the campaign's event day; DAYS_BEFORE_END = N days before sign-ups close (end date). Fixed by the step.
export const MARKETING_SEND_TIMING_TYPES = [
  'IMMEDIATE',
  'DAYS_AFTER_SUBMIT',
  'DAYS_BEFORE_EVENT',
  'DAYS_BEFORE_END',
] as const;
export type MarketingSendTimingType = (typeof MARKETING_SEND_TIMING_TYPES)[number];

// ---------------------------------------------------------------------------
// Message variables — the fixed, closed set of `{{token}}` chips the editor can insert. Anything else
// inside `{{…}}` is rejected on save (`findUnknownMessageTokens`) so a typo never reaches a client.
// ---------------------------------------------------------------------------
export const MARKETING_MESSAGE_VARIABLES = [
  'client.firstName',
  'salon.name',
  'campaign.name',
  'campaign.startDate',
  'campaign.endDate',
  'campaign.eventDate',
  'booking.link',
  'landing.url',
] as const;
export type MarketingMessageVariable = (typeof MARKETING_MESSAGE_VARIABLES)[number];

export const MARKETING_MESSAGE_VARIABLE_LABELS: Record<MarketingMessageVariable, string> = {
  'client.firstName': 'First name',
  'salon.name': 'Salon name',
  'campaign.name': 'Campaign name',
  'campaign.startDate': 'Start date',
  'campaign.endDate': 'End date',
  'campaign.eventDate': 'Event day',
  'booking.link': 'Booking link',
  'landing.url': 'Landing URL',
};

/**
 * The `{{token}}` set a campaign's journey copy may use: every campaign gets the base set; a VOUCHER campaign adds
 * the guest's own voucher (`{{voucher.offer}}`, `{{voucher.link}}`, `{{voucher.goodUntil}}`) and how to use it
 * (`{{voucher.howToUse}}`, `{{voucher.howToUseReminder}}`), a GIVEAWAY campaign its main prize, draw day + time, the
 * entrant's ticket card and the visit ticket (`{{giveaway.prize}}`, `{{giveaway.drawDate}}`, `{{giveaway.drawTime}}`,
 * `{{giveaway.cardLink}}`, `{{giveaway.visitTicketLine}}`). Saves, the publish checklist, the publish gate and the
 * dispatcher all read this one list, so a shape's token never reaches a campaign that can't fill it.
 */
export function campaignMessageVariables(shape: CampaignShape | null | undefined): readonly string[] {
  if (shape === 'VOUCHER') return [...MARKETING_MESSAGE_VARIABLES, ...VOUCHER_MESSAGE_VARIABLES];
  if (shape === 'GIVEAWAY') return [...MARKETING_MESSAGE_VARIABLES, ...GIVEAWAY_MESSAGE_VARIABLES];
  return MARKETING_MESSAGE_VARIABLES;
}

// ---------------------------------------------------------------------------
// Field caps — the editor `maxLength` + counter and the API validator read the same numbers.
// SMS body caps at 480 (a sane multi-segment marketing ceiling); email subject/body are longer.
// ---------------------------------------------------------------------------
export const AUTOMATION_MESSAGE_LIMITS = {
  smsBody: 480,
  emailSubject: 140,
  emailBody: 2000,
  ctaLabel: 40,
} as const;

// ---------------------------------------------------------------------------
// Step metadata (what each step means) lives in `automation-journey-steps.ts`.
// ---------------------------------------------------------------------------
export {
  DEFAULT_AUTOMATION_SEND_HOUR,
  JOURNEY_STEPS,
  type JourneyAnchor,
  type JourneyStepMeta,
} from './automation-journey-steps';

// ---------------------------------------------------------------------------
// Journey messages
// ---------------------------------------------------------------------------
export interface AutomationMessageTemplate {
  step: MarketingAutomationStep;
  channel: MarketingSendChannel;
  enabled: boolean;
  bodyText: string;
  /** EMAIL only. */
  emailSubject: string | null;
  /** EMAIL only: label of the booking-link button; null/blank = no button. */
  ctaLabel: string | null;
  timingType: MarketingSendTimingType;
  /** Days; 0 for IMMEDIATE. */
  timingValue: number;
  /** Salon-local hour 0-23 for day-based steps. */
  sendHour: number;
  order: number;
}

/**
 * The subset of a message the caller may edit; the step supplies timing type and order. Every editable
 * field is optional — omitted keeps the stored value, so an "enable only" edit never wipes copy.
 */
export interface AutomationMessageEdit {
  step: MarketingAutomationStep;
  channel: MarketingSendChannel;
  enabled?: boolean;
  bodyText?: string;
  emailSubject?: string | null;
  ctaLabel?: string | null;
  timingValue?: number;
  sendHour?: number;
}

/** Journey steps that start enabled on a new campaign: Welcome SMS + Follow-up SMS. "On the day" starts off. */
const DEFAULT_ENABLED_KEYS = new Set(['CONFIRMATION:SMS', 'WINBACK:SMS']);

/** Stable identity of a message within a journey. */
export function automationMessageKey(message: Pick<AutomationMessageTemplate, 'step' | 'channel'>): string {
  return `${message.step}:${message.channel}`;
}

export interface DefaultJourneyOptions {
  /** False when the salon has no online booking link: the texts ask the guest to reply to book. Default true. */
  hasBookingLink?: boolean;
  /**
   * What the salon's link is for. ORDER (a restaurant): a shape's own texts take their wording without the "Book:" part,
   * because the link opens an ordering site. Default BOOK.
   */
  bookingAction?: BookingLinkAction;
  /**
   * False when the salon's bookings live on its own POS (Calendar off): the texts say "come in" and only point to
   * booking for someone who has not booked yet. Default true.
   */
  bookingsInHayven?: boolean;
  /**
   * The campaign's shape: a voucher starts with its voucher text + a last-chance text 3 days before the end, an event
   * with a "you're in" text + an "On the day" text, a giveaway with its "you're in" text (+ the opening texts when it
   * has an event day). Omitted / null = the classic journey, unchanged.
   */
  shape?: CampaignShape | null;
  /**
   * The campaign has an event day. A giveaway's "2 days before" and "On the day" texts start on only with one.
   * Default false.
   */
  hasEventDate?: boolean;
}

/** One journey row with default copy and timing (disabled unless it is a default-on key). */
export function buildDefaultJourneyMessage(
  step: MarketingAutomationStep,
  channel: MarketingSendChannel,
  options: DefaultJourneyOptions = {}
): AutomationMessageTemplate {
  const meta = JOURNEY_STEPS[step];
  const bookingsInHayven = options.bookingsInHayven !== false;
  const copy = journeyStepDefaultCopy(step, bookingsInHayven);
  const isEmail = channel === 'EMAIL';
  const hasBookingLink = options.hasBookingLink !== false;
  const shaped = shapeJourneyMessageDefaults(options.shape, step, channel, {
    hasBookingLink: hasBookingLink && options.bookingAction !== 'ORDER',
    hasEventDate: options.hasEventDate === true,
  });
  const smsBody = shaped?.smsBody ?? (hasBookingLink ? copy.smsBody : journeyReplyToBookSms(step, bookingsInHayven));
  return {
    step,
    channel,
    enabled: shaped ? shaped.enabled : DEFAULT_ENABLED_KEYS.has(`${step}:${channel}`),
    bodyText: isEmail ? copy.emailBody : smsBody,
    emailSubject: isEmail ? copy.emailSubject : null,
    ctaLabel: isEmail ? copy.ctaLabel : null,
    timingType: meta.timingType,
    timingValue: shaped?.timingValue ?? meta.defaultTimingValue,
    sendHour: shaped?.sendHour ?? meta.defaultSendHour,
    order: MARKETING_AUTOMATION_STEPS.indexOf(step) * MARKETING_SEND_CHANNELS.length + MARKETING_SEND_CHANNELS.indexOf(channel),
  };
}

/** Every (step, channel) row in display order — what a brand-new campaign's automation starts with. */
export function buildDefaultJourney(options: DefaultJourneyOptions = {}): AutomationMessageTemplate[] {
  return MARKETING_AUTOMATION_STEPS.flatMap((step) =>
    MARKETING_SEND_CHANNELS.map((channel) => buildDefaultJourneyMessage(step, channel, options))
  );
}

/**
 * Force the step-owned fields: timing type and order come from the step; an immediate step has no day
 * offset or send hour (pinned to the defaults so they can never register as a timing change); a step whose
 * day is its meaning ("On the day") keeps its own day offset; SMS has no subject or CTA.
 */
function normalizeJourneyMessage(message: AutomationMessageTemplate): AutomationMessageTemplate {
  const defaults = buildDefaultJourneyMessage(message.step, message.channel);
  const isEmail = message.channel === 'EMAIL';
  const immediate = defaults.timingType === 'IMMEDIATE';
  const fixedDay = immediate || JOURNEY_STEPS[message.step].fixedTimingValue;
  return {
    ...message,
    emailSubject: isEmail ? message.emailSubject : null,
    ctaLabel: isEmail ? message.ctaLabel : null,
    timingType: defaults.timingType,
    timingValue: fixedDay ? defaults.timingValue : message.timingValue,
    sendHour: immediate ? defaults.sendHour : message.sendHour,
    order: defaults.order,
  };
}

type StoredJourneyRow = Pick<
  AutomationMessageTemplate,
  'step' | 'channel' | 'enabled' | 'bodyText' | 'emailSubject' | 'ctaLabel' | 'timingValue' | 'sendHour'
>;

/**
 * The full journey (every step × channel) from stored rows: each stored row keeps its copy/timing/flag, and any
 * (step, channel) with no row yet (a journey saved before a step existed) fills in from the defaults as
 * disabled — so a configured automation never silently starts sending a step the owner never turned on.
 */
export function mergeJourney(stored: readonly StoredJourneyRow[]): AutomationMessageTemplate[] {
  const byKey = new Map(stored.map((row) => [automationMessageKey(row), row]));
  return buildDefaultJourney().map((defaults) => {
    const row = byKey.get(automationMessageKey(defaults));
    if (!row) return { ...defaults, enabled: false };
    return normalizeJourneyMessage({
      ...defaults,
      enabled: row.enabled,
      bodyText: row.bodyText,
      emailSubject: row.emailSubject,
      ctaLabel: row.ctaLabel,
      timingValue: row.timingValue,
      sendHour: row.sendHour,
    });
  });
}

/** Overlay caller edits onto a full journey by (step, channel); rows without an edit are unchanged. */
export function applyJourneyEdits(
  base: readonly AutomationMessageTemplate[],
  edits: readonly AutomationMessageEdit[]
): AutomationMessageTemplate[] {
  const byKey = new Map(edits.map((edit) => [automationMessageKey(edit), edit]));
  return base.map((message) => {
    const edit = byKey.get(automationMessageKey(message));
    if (!edit) return message;
    return normalizeJourneyMessage({
      ...message,
      enabled: edit.enabled ?? message.enabled,
      bodyText: edit.bodyText ?? message.bodyText,
      emailSubject: edit.emailSubject === undefined ? message.emailSubject : edit.emailSubject,
      ctaLabel: edit.ctaLabel === undefined ? message.ctaLabel : edit.ctaLabel,
      timingValue: edit.timingValue ?? message.timingValue,
      sendHour: edit.sendHour ?? message.sendHour,
    });
  });
}
